import ClearIcon from '@mui/icons-material/Clear';
import { Box, Card, CardHeader, IconButton, Stack } from '@mui/material';
import { type FlowInputSchema } from 'flow-schema';

import { Alert, type api, colors, isDefined } from 'common';

import { buildFlowArgumentRows } from './utils';
import { BlockDynamicField } from '../../../../ConfigFields/DynamicField/DynamicField';
import { BlockDynamicFieldPlaceholder } from '../../../../ConfigFields/DynamicField/Placeholder';
import { type ScriptContextType } from '../../../../ConfigFields/DynamicField/ScriptContext';
import { useSelectedNodeField } from '../../../../ConfigFields/useSelectedNodeField';

type FlowArgument = api.FlowDefinition.FlowArgument;

export interface FlowArgumentsProps {
  fieldName: string;
  inputSchema: FlowInputSchema | undefined;
  scriptContextType?: ScriptContextType;
}

/**
 * Must be wrapped in a SubFlowFieldsProvider
 */
export function FlowArguments(props: FlowArgumentsProps) {
  const { fieldName, inputSchema, scriptContextType } = props;
  const { setValue: setFlowArguments, value: flowArguments = [] } =
    useSelectedNodeField<FlowArgument[]>(fieldName);
  const rows = buildFlowArgumentRows({ inputSchema, flowArguments });

  const deleteArgument = (argumentId: string) => {
    setFlowArguments(flowArguments.filter(({ id }) => id !== argumentId));
  };

  return (
    <Stack gap={2}>
      {rows.every(({ parameter }) => !parameter) && (
        <Box>
          <Alert title="No inputs" variant="info">
            No inputs are declared in the selected flow.
          </Alert>
        </Box>
      )}
      {rows.map(({ argument, parameter, fieldIndex }) => (
        <Card key={argument ? argument.name : parameter.name} variant="outlined">
          <CardHeader
            title={argument ? argument.name : parameter.name}
            sx={{ backgroundColor: colors.background.default, padding: 1 }}
            action={
              isDefined(argument) &&
              !isDefined(parameter) && (
                <IconButton
                  aria-label="Delete Input"
                  onClick={() => {
                    deleteArgument(argument.id);
                  }}
                  sx={{ padding: 0, marginX: 0.5 }}
                >
                  <ClearIcon />
                </IconButton>
              )
            }
          />
          <Stack gap={1} padding={1}>
            {argument ? (
              <BlockDynamicField
                fieldName={`${fieldName}.${fieldIndex}.value`}
                description={parameter?.description}
                scriptContextType={scriptContextType}
                onClear={() => {
                  if (isDefined(parameter)) {
                    deleteArgument(argument.id);
                  }
                }}
              />
            ) : (
              <BlockDynamicFieldPlaceholder
                description={parameter.description}
                scriptContextType={scriptContextType}
                onCreate={(value) => {
                  const newArgument: FlowArgument = {
                    id: parameter.id,
                    name: parameter.name,
                    value,
                  };
                  setFlowArguments([...flowArguments, newArgument]);
                }}
              />
            )}
            {isDefined(argument) && !isDefined(parameter) && (
              <Alert variant="warning">This input is not in the selected flow</Alert>
            )}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
